'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import moment from 'moment-timezone';
import {keys} from 'ts-transformer-keys';

import {FormEvent} from 'primereact/ts-helpers';
import {
    Calendar,
    CalendarPropsSingle
} from 'primereact/calendar';

import {__} from '~/shared/src/utilities/messageCatalog';
import {
    TimeFieldPrecision,
    useHookFormDelay
} from '~/shared/src/utilities/modules/form';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {StoreState} from '~/frontend/src/store';


type InputProps = Omit<CalendarPropsSingle, 'onChange'>;
const inputProps = keys<InputProps>();

const maskSlotChar = '_';

type InputChangeProps = FormEvent<Date>;

type RawModeAndPrecisionProps = {
    mask : string;
    showSeconds : boolean;
    showMillisec : boolean;
    momentFormat : string;
};

type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * The precision the timestamp is displayed and saved with.
     */
    precision? : TimeFieldPrecision,

    /**
     * Should the value shown as defined (raw) <strong>or</strong> should they be formatted.
     */
    rawMode? : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    precision      : 'seconds',
    rawMode        : false,
    markDirty      : false,
    inputClassNames: [],
    inputStyle     : {},
    useHookForm    : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : Date;

    /**
     * The value to determine dirty against.
     */
    originalValue? : Date;

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : Date | undefined, originalEvent : any ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : Date | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const TimeField = React.forwardRef<Calendar, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  uid,
                  appendToElementId,
                  id,
                  inputClassNames,
                  inputStyle,
                  markDirty,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  originalValue,
                  placeholder,
                  precision,
                  pt,
                  rawMode,
                  tooltipDisabled,
                  useHookForm,
                  value
              }                                 = props,
              elementId                         = id ?? `${uid}_timeField`,
              inputRef                          = useRef<Calendar | null>( null ),
              inValue                           = useRef<Date | undefined>( value ),
              outValue                          = useRef<Date | undefined>( value ),
              timeoutId                         = useRef<NodeJS.Timeout | undefined>(),
              [ elementValue, setElementValue ] = useState<Date | undefined>( value ),
              [ focus, setFocus ]               = useState<boolean>( false ),
              [ appendTo, setAppendTo ]         = useState<'self' | HTMLElement>( 'self' ),
              [ overlay, setOverlay ]           = useState<boolean>( false ),
              messageResolver                   = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder               = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              // compute only the first time, since mask property of Calendar is not updatable
              rawModeAndPrecisionProps          = useMemo(
                  () => {
                      const retVal : RawModeAndPrecisionProps = {
                          mask        : '99:99',
                          showSeconds : false,
                          showMillisec: false,
                          momentFormat: 'HH:mm'
                      };

                      if ( rawMode ) {
                          retVal.mask = '99:99:99.999999';
                          retVal.showSeconds = true;
                          retVal.showMillisec = true;
                          retVal.momentFormat = 'HH:mm:ss.SSSSSS';
                      }
                      else if ( precision === 'seconds' ) {
                          retVal.mask = '99:99:99';
                          retVal.showSeconds = true;
                          retVal.momentFormat = 'HH:mm:ss';
                      }
                      else if ( precision === 'milliseconds' ) {
                          retVal.mask = '99:99:99.999';
                          retVal.showSeconds = true;
                          retVal.showMillisec = true;
                          retVal.momentFormat = 'HH:mm:ss.SSS';
                      }
                      else if ( precision === 'microseconds' ) {
                          retVal.mask = '99:99:99.999999';
                          retVal.showSeconds = true;
                          retVal.showMillisec = true;
                          retVal.momentFormat = 'HH:mm:ss.SSSSSS';
                      }

                      return retVal;
                  },
                  [
                      precision,
                      rawMode
                  ]
              ),
              buildChangedValue                 = useCallback(
                  ( event : InputChangeProps ) => {
                      const input = inputRef.current?.getInput?.() as unknown as HTMLInputElement;

                      let retVal : Date | undefined;

                      if ( event.value !== undefined && event.value !== null && ( !input || input.value.indexOf( maskSlotChar ) === -1 ) ) {
                          // get in local timezone
                          retVal = moment( event.value as Date ).toDate();
                      }

                      return retVal;
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onInputChange                     = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = buildChangedValue( event );

                      if ( changedValue !== outValue.current ) {
                          outValue.current = changedValue;

                          if ( changedValue ) {
                              setElementValue( changedValue );
                          }

                          if ( useHookForm ) {
                              onIntermediateChange?.( changedValue, event );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                          }
                          else {
                              onChange( changedValue, event );
                          }
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange,
                      useHookForm
                  ]
              ),
              onInputFocus                      = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> ) => {
                      setFocus( true );

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                       = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> ) => {
                      setFocus( false );

                      setElementValue( outValue.current );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event );
                  },
                  [
                      onBlur,
                      onChange
                  ]
              ),
              onInputShow                       = useCallback(
                  () => {
                      let overlayElement : HTMLElement | undefined,
                          targetElement : HTMLElement | undefined;

                      setOverlay( true );

                      if ( inputRef.current?.getOverlay() ) {
                          overlayElement = inputRef.current.getOverlay();
                          targetElement = inputRef.current.getElement();
                      }

                      if ( overlayElement && targetElement ) {
                          if ( overlayElement.style.transformOrigin.indexOf( 'bottom' ) !== -1 ) {
                              overlayElement.style.marginTop = '-2px';
                          }
                          else {
                              overlayElement.style.marginTop = '2px';
                          }
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onInputHide                       = useCallback(
                  () => {
                      setOverlay( false );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onInputPaste                      = useCallback(
                  ( event : React.ClipboardEvent<HTMLInputElement> ) => {
                      const input = inputRef.current?.getInput?.() as unknown as HTMLInputElement;

                      if ( input ) {
                          const start             = input.selectionStart ?? 0,
                                currentInputValue = input.value,
                                end               = input.selectionEnd ?? 0,
                                data              = event.clipboardData.getData( 'text/plain' ),
                                newInputValue     = currentInputValue.substring( 0, start ) + data + currentInputValue.substring( end ),
                                momentDate        = moment( newInputValue, rawModeAndPrecisionProps.momentFormat ),
                                changedValue      = momentDate?.isValid() ? momentDate.tz( 'utc', true ).toDate() : undefined;

                          input.value = newInputValue;
                          input.setSelectionRange( end, end );

                          if ( changedValue && changedValue !== outValue.current ) {
                              outValue.current = changedValue;

                              if ( changedValue ) {
                                  setElementValue( changedValue );
                              }

                              if ( useHookForm ) {
                                  onIntermediateChange?.( changedValue, event );

                                  if ( timeoutId.current ) {
                                      clearTimeout( timeoutId.current );
                                  }
                                  timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                              }
                              else {
                                  onChange( changedValue, event );
                              }
                          }

                          event.preventDefault();
                          event.stopPropagation();
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange,
                      rawModeAndPrecisionProps,
                      useHookForm
                  ]
              );

        inValue.current = value;
        outValue.current = elementValue;

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        // resolve appendTo
        useLayoutEffect(
            () => {
                if ( appendToElementId && appendToElementId !== 'self' ) {
                    let appendToElement : HTMLElement | null;

                    if ( appendToElementId !== 'document' ) {
                        appendToElement = document.getElementById( appendToElementId );
                    }
                    else {
                        appendToElement = document.body;
                    }

                    if ( appendToElement && appendToElement !== appendTo ) {
                        setAppendTo( appendToElement );
                    }
                }
            }
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && elementValue?.getTime() !== originalValue?.getTime()}

            displayValueWidthOffsetRem={2.357}
            displayValueWidthOffsetPx={1}
            tooltipDisabled={tooltipDisabled || overlay}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            <Calendar
                {..._.pick( props, inputProps ) as InputProps}

                id={elementId}

                ref={( node ) => {
                    inputRef.current = node;
                    if ( typeof forwardedRef === 'function' ) {
                        forwardedRef( node );
                    }
                    else if ( forwardedRef ) {
                        forwardedRef.current = node;
                    }
                }}

                value={elementValue}
                onChange={onInputChange}
                onShow={onInputShow}
                onHide={onInputHide}

                pt={{
                    input: {
                        root: {
                            onFocus       : onInputFocus,
                            onBlur        : onInputBlur,
                            onPasteCapture: onInputPaste
                        }
                    }
                }}

                className={`yn-timefield ${inputClassNames.join( ' ' )}${rawMode ? ' yn-rawmode' : ''}${focus ? ' p-focus' : ''}`}
                style={{ ...defaultProps.inputStyle, ...inputStyle }}

                placeholder={resolvedPlaceholder}
                tooltip={undefined}
                tooltipOptions={undefined}

                aria-describedby={`${elementId}_message`}

                showIcon
                showOnFocus={false}
                timeOnly
                showTime
                hourFormat="24"
                showSeconds={rawModeAndPrecisionProps.showSeconds}
                showMillisec={rawModeAndPrecisionProps.showMillisec}
                maskSlotChar={maskSlotChar}
                mask={rawModeAndPrecisionProps.mask}

                appendTo={appendTo}
                panelClassName={appendTo !== 'self' ? 'yn-overlay-panel-positioned' : undefined}
            />
        </FieldWrapper>;
    }
} );
TimeField.defaultProps = defaultProps;
TimeField.displayName = 'TimeField';

export default TimeField;
